<template>
  <div
    :class="classes(n(), { [n('--disabled')]: disabled })"
    role="group"
    :aria-disabled="disabled ? 'true' : undefined"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <div
      :class="n('content')"
      :style="contentStyle"
      @click="handleContentClick"
    >
      <slot />
    </div>

    <div
      v-if="actions.length"
      ref="actionsRef"
      :class="n('actions')"
      :style="actionsStyle"
    >
      <div
        v-for="(action, index) in actions"
        :key="action.key"
        role="button"
        :class="
          classes(
            n('action'),
            n(`action--${action.layout || 'text'}`),
            isPresetColor(action) ? n(`action--${action.color || 'blue'}`) : ''
          )
        "
        :style="actionStyle(action, index)"
        :aria-label="getAriaLabel(action)"
        @click="handleActionClick(action, index)"
      >
        <div :class="n('action-inner')" :style="actionInnerStyle">
          <component
            :is="action.icon"
            v-if="action.icon && action.layout !== 'text'"
            :class="n('action-icon')"
            :width="ACTION_ICON_SIZE"
            :height="ACTION_ICON_SIZE"
            :color="ACTION_ICON_COLOR"
          />
          <span
            v-if="showText(action)"
            :class="n('action-text')"
            aria-live="polite"
          >
            {{ actionText(action) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
  defineComponent,
  isVue3,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { useSwipeTouch } from './use-swipe-touch';
import type {
  SwipeAction,
  SwipeCellState,
  SwipeCloseReason,
  BeforeCloseParams,
} from './swipe-cell';

const { name, n, classes } = createNamespace('swipe-cell');

/** 预设配色集合，命中后走 Token class，否则作为自定义背景色 */
const PRESET_COLORS = ['blue', 'orange', 'red'];
/** 展开 / 收起动画时长（ms），与主题变量保持一致 */
const TRANSITION_DURATION = 300;
/**
 * 操作按钮图标尺寸（设计单位，与主题变量 @swipe-cell-action-icon-size 一致）。
 * wphk-icon 组件按 v/200 rem 换算（即 @unit=0.005rem），故 40 即 40 设计单位的方形图标。
 * 显式传入 width/height 可强制方形，避免图标按 viewBox 比例渲染成非方形。
 */
const ACTION_ICON_SIZE = 40;
/**
 * 操作按钮图标颜色。预设配色（蓝/橙/红）按钮文字均为白色，图标同步为白色。
 * 需显式传入：wphk-icon 默认取 currentColor，但 LiteApp 下 SVG mask 无法解析 currentColor 会渲染成黑色。
 */
const ACTION_ICON_COLOR = '#FFFFFF';
/**
 * 单个操作按钮默认宽度（rem），与主题变量 @swipe-cell-action-width（120 * @unit）一致：
 * 120 * 0.005rem = 0.6rem。用于在不依赖 max-content 的情况下确定性计算自然总宽。
 */
const ACTION_WIDTH_REM = 0.6;

/** 读取根字号（px/rem）；不可用时返回 0，由调用方回退到 DOM 测量 */
const getRootFontSize = (): number => {
  if (typeof document === 'undefined' || !document.documentElement) return 0;
  let raw = '';
  if (typeof getComputedStyle === 'function') {
    raw = getComputedStyle(document.documentElement).fontSize || '';
  }
  if (!raw) {
    raw = document.documentElement.style.fontSize || '';
  }
  const fs = Number.parseFloat(raw);
  return Number.isFinite(fs) ? fs : 0;
};

/** 单个操作按钮的自然宽度（px）：自定义 width 优先，否则取设计宽度 0.6rem */
const getActionWidth = (action: SwipeAction, rootPx: number): number => {
  if (action.width) return action.width;
  return ACTION_WIDTH_REM * rootPx;
};

export default defineComponent({
  name,
  props,
  emits,
  setup(props, { emit }) {
    /** 右侧操作区 DOM 引用，用于测量实际宽度 */
    const actionsRef = ref<HTMLElement | null>(null);
    /** 缓存的操作区宽度（px） */
    const actionsWidth = ref(0);
    /** 内部展开状态 */
    const currentState = ref<SwipeCellState>('closed');
    /** 当前处于二次确认态的操作项 key（null 表示无） */
    const confirmingKey = ref<string | number | null>(null);

    // ==================== v-model 兼容层 ====================

    /** 读取当前 v-model 绑定值，兼容 Vue2/3 */
    const getModelValue = (): SwipeCellState => (isVue3 ? props.modelValue : (props as Record<string, unknown>).value as SwipeCellState) ?? 'closed';

    /** 向外 emit v-model 值变更，同时触发 Vue3 / Vue2 事件 */
    const emitModelValue = (val: SwipeCellState) => {
      emit('update:modelValue', val);
      emit('input', val);
    };

    // ==================== 宽度测量 ====================

    /**
     * 每个按钮「自身 + 其右侧所有按钮」的宽度和（px），下标与 actions 对齐。
     * 用于按钮从右侧滑入的位移量：translateX = (1 - 进度) × rightRemain[index]，
     * 收起时所有按钮堆到屏外右侧，滑动时所有按钮一起等比露出。
     */
    const actionRightRemain = ref<number[]>([]);

    /**
     * 测量操作区自然宽（px）= 各按钮宽度之和，并计算每个按钮的宽度与「右侧剩余宽度和」。
     *
     * 按钮宽度随内容自适应（仅靠 translateX 平移，不改自然宽），随时可读到自然宽，直接读取即可。
     */
    const measureWidth = (): number => {
      const el = actionsRef.value;
      if (el && el.children && typeof el.getBoundingClientRect === 'function') {
        const children = el.children as HTMLCollectionOf<HTMLElement>;
        const count = children.length;
        const widths: number[] = [];
        for (let i = 0; i < count; i += 1) {
          widths.push(
            typeof children[i].getBoundingClientRect === 'function'
              ? children[i].getBoundingClientRect().width
              : 0,
          );
        }
        const total = widths.reduce((sum, w) => sum + w, 0);
        if (total > 0) {
          // 后缀和：rightRemain[i] = widths[i] + widths[i+1] + ... + widths[n-1]
          const remain: number[] = new Array(count);
          let acc = 0;
          for (let i = count - 1; i >= 0; i -= 1) {
            acc += widths[i];
            remain[i] = acc;
          }
          actionRightRemain.value = remain;
          actionsWidth.value = total;
        }
      }
      return actionsWidth.value;
    };

    /** 展开进度 [0,1]：滑动距离 / 自然总宽 */
    const openProgress = computed(() => {
      const total = actionsWidth.value;
      if (total <= 0) return 0;
      return Math.min(1, Math.max(0, -touch.offset.value / total));
    });

    /** 越过完全展开位置后的额外位移（px，已带阻尼），用于横向拉伸操作区 */
    const openExtra = computed(() => {
      const total = actionsWidth.value;
      if (total <= 0) return 0;
      return Math.max(0, -touch.offset.value - total);
    });

    // ==================== 触摸交互 ====================

    const touch = useSwipeTouch({
      // 拖动过程用缓存自然宽，避免每次 move 触发 max-content 重排
      getWidth: () => actionsWidth.value || measureWidth(),
      isDisabled: () => props.disabled,
      getThreshold: () => props.threshold,
      onSwipeEnd: (shouldOpen: boolean) => {
        if (shouldOpen) {
          setOpen();
        } else {
          setClosed('swipe');
        }
      },
    });

    /** 内容区位移样式：随手指左移；拖动时无过渡，吸附 / 受控时启用过渡 */
    const contentStyle = computed(() => ({
      transform: `translate3d(${touch.offset.value}px, 0, 0)`,
      transition: touch.dragging.value
        ? 'none'
        : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.18, 0.89, 0.32, 1)`,
    }));

    /** 越界超滑时操作区容器的横向缩放倍数（>=1），以右边缘为原点整体拉伸 */
    const overscrollScale = computed(() => {
      const total = actionsWidth.value;
      return total > 0 ? 1 + openExtra.value / total : 1;
    });

    /**
     * 操作区容器样式：越界超滑时 scaleX 横向拉伸。
     * 用 transform 走合成层，不触发按钮逐帧重排（避免抖动），
     * 子按钮作为整体缩放、相邻边界不产生亚像素缝（避免白线）。松手回弹到 scaleX(1)。
     */
    const actionsStyle = computed(() => ({
      transform: `scaleX(${overscrollScale.value})`,
      transition: touch.dragging.value
        ? 'none'
        : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.18, 0.89, 0.32, 1)`,
    }));

    /**
     * 按钮内容（图标+文字）样式：施加与容器相反的 scaleX(1/scale)，
     * 抵消父级横向拉伸，使背景跟着拉伸而文字/图标保持原始尺寸并居中。
     */
    const actionInnerStyle = computed(() => ({
      transform: `scaleX(${1 / overscrollScale.value})`,
      transition: touch.dragging.value
        ? 'none'
        : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.18, 0.89, 0.32, 1)`,
    }));

    // ==================== 状态切换 ====================

    /** 展开操作区，emitEvent=false 时仅同步内部状态（用于受控回流） */
    const setOpen = (emitEvent = true) => {
      // 优先用缓存自然宽：越界超滑时按钮已被拉伸，重新测量会读到拉伸后的宽度导致无法回弹
      const width = actionsWidth.value || measureWidth();
      touch.offset.value = -width;
      if (currentState.value !== 'open') {
        currentState.value = 'open';
        if (emitEvent) {
          emitModelValue('open');
          emit('open', { name: props.name });
        }
      }
    };

    /** 收起操作区并重置确认态，emitEvent=false 时仅同步内部状态 */
    const setClosed = (reason: SwipeCloseReason, emitEvent = true) => {
      touch.offset.value = 0;
      confirmingKey.value = null;
      if (currentState.value !== 'closed') {
        currentState.value = 'closed';
        if (emitEvent) {
          emitModelValue('closed');
          emit('close', { name: props.name, reason });
        }
      }
    };

    // ==================== beforeClose 拦截 ====================

    /** 执行 beforeClose，返回是否放行（异常 / false 均视为拦截） */
    const runBeforeClose = async (params: BeforeCloseParams): Promise<boolean> => {
      const fn = props.beforeClose;
      if (typeof fn !== 'function') return true;
      try {
        const result = await fn(params);
        return result !== false;
      } catch {
        return false;
      }
    };

    // ==================== 渲染辅助 ====================

    /** 是否为预设配色（blue/orange/red） */
    const isPresetColor = (action: SwipeAction): boolean => PRESET_COLORS.includes(action.color || 'blue');

    /**
     * 操作按钮内联样式：固定宽度 + 从右侧滑入的位移。
     *
     * translateX = (1 - 进度) × 该按钮右侧剩余宽度和：
     * 收起（进度 0）时每个按钮都平移到屏外右侧堆叠，展开（进度 1）时归位；
     * 所有按钮同一进度 → 一起等比露出/收起，超出右边界部分由根节点 overflow 裁剪。
     */
    const actionStyle = (action: SwipeAction, index: number) => {
      const remain = actionRightRemain.value[index] ?? 0;
      const translate = (1 - openProgress.value) * remain;
      const style: Record<string, string> = {
        transform: `translate3d(${translate}px, 0, 0)`,
        transition: touch.dragging.value
          ? 'none'
          : `transform ${TRANSITION_DURATION}ms cubic-bezier(0.18, 0.89, 0.32, 1)`,
      };
      if (action.width) {
        style.width = `${action.width}px`;
      }
      if (action.color && !isPresetColor(action)) {
        style.backgroundColor = action.color;
      }
      return style;
    };

    /** 是否启用原地二次确认（danger 且未显式关闭） */
    const useInlineConfirm = (action: SwipeAction): boolean => !!action.danger && action.confirm !== 'none';

    /** 当前按钮展示文案：原地确认态显示 confirmText，否则显示 text */
    const actionText = (action: SwipeAction): string => {
      if (useInlineConfirm(action) && confirmingKey.value === action.key) {
        return action.confirmText || '确认';
      }
      return action.text || '';
    };

    /** 是否渲染文字（纯图标布局不渲染文字） */
    const showText = (action: SwipeAction): boolean => (action.layout || 'text') !== 'icon' && !!actionText(action);

    /** 无障碍标签：优先确认态文案 → text → ariaLabel → key */
    const getAriaLabel = (action: SwipeAction): string | undefined => actionText(action) || action.ariaLabel || String(action.key);

    // ==================== 交互处理 ====================

    /**
     * 操作按钮点击：
     * - danger 且 confirm=inline：首次进入原地确认态，再次点击经 beforeClose 抛出 action
     * - danger 且 confirm=none / 非危险操作：直接经 beforeClose 抛出 action
     */
    const handleActionClick = async (action: SwipeAction, index: number) => {
      emit('click', 'action');

      // 原地确认：首次点击仅切换为确认态，不抛 action
      if (useInlineConfirm(action) && confirmingKey.value !== action.key) {
        confirmingKey.value = action.key;
        return;
      }

      const proceed = await runBeforeClose({
        reason: 'click-action',
        action,
        name: props.name,
      });
      if (!proceed) return;

      emit('action', action, { index, name: props.name });
      confirmingKey.value = null;
      if (props.closeOnClickAction) {
        setClosed('click-action');
      }
    };

    /** 内容区点击：展开态下收起并阻断穿透，否则抛 click('cell') */
    const handleContentClick = (event: MouseEvent) => {
      if (currentState.value === 'open') {
        if (props.closeOnClickCell) {
          if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
          }
          setClosed('click-cell');
        }
        return;
      }
      emit('click', 'cell');
    };

    // ==================== 受控同步 ====================

    watch(getModelValue, (val) => {
      if (val === currentState.value) return;
      if (val === 'open') {
        setOpen(false);
      } else {
        setClosed('method', false);
      }
    });

    // ==================== 生命周期 ====================

    onMounted(() => {
      nextTick(() => {
        measureWidth();
        if (getModelValue() === 'open') {
          setOpen(false);
        }
      });
    });

    /** actions 变化时重新测量宽度，并对展开态重新吸附 */
    watch(
      () => props.actions,
      () => {
        nextTick(() => {
          measureWidth();
          if (currentState.value === 'open') {
            setOpen(false);
          }
        });
      },
      { deep: true },
    );

    // 进入 / 退出原地二次确认态时按钮文案变化（如「删除」→「确认删除」）会改变自然宽，
    // 需重新测量并对展开态重新吸附，使内容位移与新按钮宽对齐，避免按钮左侧压住内容、文字看似偏移。
    watch(confirmingKey, () => {
      nextTick(() => {
        measureWidth();
        if (currentState.value === 'open') {
          setOpen(false);
        }
      });
    });

    // ==================== 暴露方法 ====================

    /** 展开操作区 */
    const open = () => {
      setOpen();
    };

    /** 收起操作区 */
    const close = () => {
      setClosed('method');
    };

    return {
      n,
      classes,
      ACTION_ICON_SIZE,
      ACTION_ICON_COLOR,
      actionsRef,
      contentStyle,
      actionsStyle,
      actionInnerStyle,
      isPresetColor,
      actionStyle,
      actionText,
      showText,
      getAriaLabel,
      handleActionClick,
      handleContentClick,
      onTouchStart: touch.onTouchStart,
      onTouchMove: touch.onTouchMove,
      onTouchEnd: touch.onTouchEnd,
      open,
      close,
    };
  },
});
</script>
